import { Box, styled } from "@mui/material";
import { forwardRef, InputHTMLAttributes } from "react";

interface MMOTextFieldProps {
  label: string;
  defaultValue: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  size?: string;
}

const MMOTextField = forwardRef<HTMLInputElement|null, MMOTextFieldProps>(({ label, defaultValue, inputProps, size }: MMOTextFieldProps, ref) => {
  return (
    <MMOTextFieldRoot {...{ownerState: {hasValue: !!defaultValue, size}}}>
      <label>{label}</label>
      <div className="OutlinedInput-root">
        <input readOnly {...inputProps} ref={ref} value={defaultValue} />
        <fieldset aria-hidden >
          <legend >
            {/* Use the nominal use case of the legend, avoid rendering artefacts. */}
            <span>{label}</span>
          </legend>
        </fieldset>
      </div>
    </MMOTextFieldRoot>
  )
})

export default MMOTextField;

function shouldForwardProp(prop: string) {
  return prop !== 'ownerState' && prop !== 'theme' && prop !== 'sx' && prop !== 'as';
}

const MMOTextFieldRoot = styled(Box, {
  shouldForwardProp: (pname:string) => shouldForwardProp(pname) || pname === 'ownerState',
})(({theme, ...rest}) => {
  const hasValue = !!(rest as any).ownerState?.hasValue
  const size = (rest as any).ownerState?.size
  return ({
    display: 'inline-flex',
    flexDirection: 'column',
    position: 'relative',
    minWidth: 0,
    padding: 0,
    margin: 0,
    border: 0,
    verticalAlign: 'top',
    marginTop: 1,
    marginBottom: 1,
    '& > label': {
      color: theme.palette.text.secondary,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.4375em',
      padding: 0,
      display: 'block',
      transformOrigin: 'top left',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      position: 'absolute',
      left: 0,
      top: 0,
      transition: 'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, max-width 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
      zIndex: 1,
      pointerEvents: 'none',
      maxWidth: 'calc(133% - 24px)',
      transform: 'translate(14px, -9px) scale(0.75)',
      
      ...(!hasValue && {
        maxWidth: 'calc(100% - 24px)',
        transform: 
          size === 'small'  
          ? 'translate(14px, 9px) scale(1)'
          : 'translate(14px, 16px) scale(1)',
      })
    },
    '& > div.OutlinedInput-root': {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: '1.4375em',
      color: theme.palette.text.primary,
      boxSizing: 'border-box',
      cursor: 'text',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      borderRadius: '16px',

      '& > input': {
        font: 'inherit',
        letterSpacing: 'inherit',
        color: 'currentColor',
        border: 0,
        boxSizing: 'content-box',
        background: 'none',
        height: '1.4375em',
        margin: 0,
        ...{'-webkit-tap-highlight-color': 'transparent'},
        display: 'block',
        minWidth: 0,
        width: '100%',
        animationName: 'mui-auto-fill-cancel',
        animationDuration: '10ms',
        padding: '16.5px 14px',
        ...(size === 'small' && {padding: '8.5px 14px' }),

        '&:focus': {
          outline: 0,
        }
      },

      '& > fieldset': {
        textAlign: 'left',
        position: 'absolute',
        bottom: 0,
        right: 0,
        top: '-5px',
        left: 0,
        margin: 0,
        padding: '0 8px',
        pointerEvents: 'none',
        borderRadius: 'inherit',
        borderStyle: 'solid',
        borderWidth: '1px',
        overflow: 'hidden',
        minWidth: '0%',
        borderColor: theme.palette.action.disabled,

        '& > legend': {
          float: 'unset',
          display: 'block',
          width: 'auto',
          padding: 0,
          height: '11px',
          fontSize: '0.75em',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          transition: 'max-width 100ms cubic-bezier(0.0, 0, 0.2, 1) 50ms',
          
          ...(!hasValue && {
            maxWidth: '0.01px',
            transition: 'max-width 50ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
          }),

          '& > span': {
            paddingLeft: '5px',
            paddingRight: '5px',
            display: 'inline-block',
          },
        },
      },
    },
  })
})